import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Clock,
  User,
  Server,
  FileText,
  ExternalLink,
  Filter,
  GitCompare,
  Plus,
  Minus,
  RefreshCw,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/core/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Badge } from '@/core/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/core/components/ui/table';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Checkbox } from '@/core/components/ui/checkbox';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/core/components/ui/hover-card';
import { ScrollArea } from '@/core/components/ui/scroll-area';
import { ApplicationTypeSelect } from '../atoms/ApplicationTypeSelect';
import { useVersionStore } from '../../stores/version-store';
import { useTranslationStore } from '../../stores/translation-store';
import { servicesContainer } from '@/core/di/services-container';
import {
  ApplicationType,
  S3Environment,
  S3EnvironmentLabels,
  TranslationVersion,
} from '../../types/translation-types';

export function VersionHistoryPage() {
  const navigate = useNavigate();

  // Use shared app type from translation store
  const { selectedAppType, setSelectedAppType } = useTranslationStore();

  const {
    versions,
    environmentFilter,
    isLoading,
    error,
    fetchVersions,
    setSelectedAppType: setVersionSelectedAppType,
    setEnvironmentFilter,
  } = useVersionStore();

  // Diff comparison state
  const [selectedForDiff, setSelectedForDiff] = useState<Set<number>>(new Set());
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [diffResult, setDiffResult] = useState<{
    added: { key: string; newValue: string }[];
    removed: { key: string; oldValue: string }[];
    changed: { key: string; oldValue: string; newValue: string; fullChanges: string }[];
    oldVersion: TranslationVersion;
    newVersion: TranslationVersion;
  } | null>(null);
  const [expandedChanges, setExpandedChanges] = useState<Set<string>>(new Set());

  // Sync version store app type and fetch versions when app type changes
  useEffect(() => {
    if (selectedAppType !== null) {
      setVersionSelectedAppType(selectedAppType);
      fetchVersions();
    }
  }, [fetchVersions, selectedAppType, setVersionSelectedAppType, environmentFilter]);

  const handleAppTypeChange = useCallback((appType: ApplicationType | null) => {
    setSelectedAppType(appType);
    if (appType !== null) {
      setVersionSelectedAppType(appType);
    }
  }, [setSelectedAppType, setVersionSelectedAppType]);

  const handleEnvironmentFilterChange = useCallback((value: string) => {
    if (value === 'all') {
      setEnvironmentFilter(null);
    } else {
      setEnvironmentFilter(parseInt(value) as S3Environment);
    }
  }, [setEnvironmentFilter]);

  // Diff comparison handlers
  const handleToggleDiffSelection = useCallback((versionId: number) => {
    setSelectedForDiff((prev) => {
      const next = new Set(prev);
      if (next.has(versionId)) {
        next.delete(versionId);
      } else if (next.size < 2) {
        next.add(versionId);
      }
      return next;
    });
  }, []);

  const handleClearDiffSelection = useCallback(() => {
    setSelectedForDiff(new Set());
  }, []);

  const handleCompareVersions = useCallback(async () => {
    if (selectedForDiff.size !== 2) return;

    const selectedIds = Array.from(selectedForDiff);
    const selectedVersions = versions
      .filter((v) => selectedIds.includes(v.versionId))
      .sort((a, b) => a.version - b.version); // Sort by version number

    if (selectedVersions.length !== 2) return;

    const [oldVersion, newVersion] = selectedVersions;

    setDiffLoading(true);
    setDiffError(null);
    setExpandedChanges(new Set());
    setDiffDialogOpen(true);

    try {
      // Fetch version details from backend API (includes jsonSnapshot)
      const services = servicesContainer.getServices();
      const [oldVersionDetail, newVersionDetail] = await Promise.all([
        services.getVersionContentUseCase.execute(oldVersion.versionId),
        services.getVersionContentUseCase.execute(newVersion.versionId),
      ]);

      if (!oldVersionDetail.jsonSnapshot || !newVersionDetail.jsonSnapshot) {
        throw new Error('Version data not available');
      }

      const oldData = JSON.parse(oldVersionDetail.jsonSnapshot);
      const newData = JSON.parse(newVersionDetail.jsonSnapshot);

      // Debug: check the actual value structure
      const firstLang = Object.keys(oldData.languages || {})[0];
      const firstKey = firstLang ? Object.keys(oldData.languages[firstLang] || {})[0] : null;
      const sampleValue = firstLang && firstKey ? oldData.languages[firstLang][firstKey] : null;
      console.log('Sample value structure:', typeof sampleValue, sampleValue);

      // Structure: { languages: { en: { key1: value1, ... }, he: { key1: value1, ... } }, lanVersion: number }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      type LanguagesData = { languages?: Record<string, Record<string, any>> };

      // Helper to extract display-friendly string from a value
      const extractDisplayValue = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') {
          const obj = value as Record<string, unknown>;
          // Try common property names for the actual text value
          if (typeof obj.value === 'string') return obj.value;
          if (typeof obj.text === 'string') return obj.text;
          if (typeof obj.translation === 'string') return obj.translation;
          // Return empty if no recognizable text field
          return '';
        }
        return String(value);
      };

      // Helper to get a normalized string for comparison (preserves all data)
      const getNormalizedValue = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') {
          return JSON.stringify(value, Object.keys(value as object).sort());
        }
        return String(value);
      };

      // Helper to compare two raw values and return changed fields
      const getChangedFields = (oldVal: unknown, newVal: unknown): { field: string; oldText: string; newText: string }[] => {
        const changes: { field: string; oldText: string; newText: string }[] = [];

        // If both are objects, compare field by field
        if (typeof oldVal === 'object' && oldVal !== null && typeof newVal === 'object' && newVal !== null) {
          const oldObj = oldVal as Record<string, unknown>;
          const newObj = newVal as Record<string, unknown>;
          const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

          for (const key of allKeys) {
            const oldFieldVal = oldObj[key];
            const newFieldVal = newObj[key];
            const oldStr = typeof oldFieldVal === 'string' ? oldFieldVal : JSON.stringify(oldFieldVal ?? '');
            const newStr = typeof newFieldVal === 'string' ? newFieldVal : JSON.stringify(newFieldVal ?? '');

            if (oldStr !== newStr) {
              changes.push({ field: key, oldText: oldStr, newText: newStr });
            }
          }
        } else {
          // Simple value comparison
          const oldStr = extractDisplayValue(oldVal) || getNormalizedValue(oldVal);
          const newStr = extractDisplayValue(newVal) || getNormalizedValue(newVal);
          if (oldStr !== newStr) {
            changes.push({ field: '', oldText: oldStr, newText: newStr });
          }
        }

        return changes;
      };

      // Helper to compare two normalized strings for equality
      const valuesEqual = (a: string, b: string): boolean => {
        if (a.startsWith('{') && b.startsWith('{')) {
          try {
            const objA = JSON.parse(a);
            const objB = JSON.parse(b);
            return JSON.stringify(objA, Object.keys(objA).sort()) === JSON.stringify(objB, Object.keys(objB).sort());
          } catch {
            // Fall through to string comparison
          }
        }
        return a === b;
      };

      // Transform data: from { languages: { en: { key: value }, he: { key: value } } }
      // to Map<key, Map<langCode, rawValue>> - keep raw values for field-by-field comparison
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const getTranslationsMapRaw = (data: LanguagesData): Map<string, Map<string, any>> => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = new Map<string, Map<string, any>>();
        const languages = data.languages || {};

        // Iterate over each language
        for (const [langCode, translations] of Object.entries(languages)) {
          // Iterate over each key-value in this language
          for (const [key, value] of Object.entries(translations)) {
            if (!result.has(key)) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              result.set(key, new Map<string, any>());
            }
            result.get(key)!.set(langCode, value); // Keep raw value
          }
        }

        console.log('getTranslationsMap: found', result.size, 'keys across', Object.keys(languages).length, 'languages');
        return result;
      };

      const oldTranslations = getTranslationsMapRaw(oldData as LanguagesData);
      const newTranslations = getTranslationsMapRaw(newData as LanguagesData);

      const oldKeys = new Set(oldTranslations.keys());
      const newKeys = new Set(newTranslations.keys());

      const added: { key: string; newValue: string }[] = [];
      const removed: { key: string; oldValue: string }[] = [];
      const changed: { key: string; oldValue: string; newValue: string; fullChanges: string }[] = [];

      // Find added keys
      for (const key of newKeys) {
        if (!oldKeys.has(key)) {
          const rawValue = newTranslations.get(key)?.get('en') || newTranslations.get(key)?.values().next().value;
          const displayValue = extractDisplayValue(rawValue) || getNormalizedValue(rawValue);
          added.push({ key, newValue: displayValue });
        }
      }

      // Find removed keys
      for (const key of oldKeys) {
        if (!newKeys.has(key)) {
          const rawValue = oldTranslations.get(key)?.get('en') || oldTranslations.get(key)?.values().next().value;
          const displayValue = extractDisplayValue(rawValue) || getNormalizedValue(rawValue);
          removed.push({ key, oldValue: displayValue });
        }
      }

      // Find changed keys - compare all language values field by field
      for (const key of oldKeys) {
        if (newKeys.has(key)) {
          const oldValues = oldTranslations.get(key)!;
          const newValues = newTranslations.get(key)!;

          // Format changes showing all languages that differ
          const truncatedChanges: string[] = [];
          const fullChangesArr: { langCode: string; changes: { field: string; oldText: string; newText: string }[] }[] = [];
          const allLangCodes = new Set([...oldValues.keys(), ...newValues.keys()]);

          for (const langCode of allLangCodes) {
            const oldRawVal = oldValues.get(langCode);
            const newRawVal = newValues.get(langCode);

            // Get field-by-field changes
            const fieldChanges = getChangedFields(oldRawVal, newRawVal);

            if (fieldChanges.length > 0) {
              fullChangesArr.push({ langCode, changes: fieldChanges });

              // Create truncated summary for collapsed view
              for (const change of fieldChanges) {
                const fieldLabel = change.field ? `${change.field}: ` : '';
                const displayOld = change.oldText.length > 30 ? change.oldText.substring(0, 30) + '...' : change.oldText;
                const displayNew = change.newText.length > 30 ? change.newText.substring(0, 30) + '...' : change.newText;
                truncatedChanges.push(`[${langCode}] ${fieldLabel}"${displayOld}" → "${displayNew}"`);
              }
            }
          }

          // Only add to changed if there are actual differences
          if (truncatedChanges.length > 0) {
            // Format fullChanges as JSON for parsing in UI
            changed.push({
              key,
              oldValue: extractDisplayValue(oldValues.get('en')) || '',
              newValue: truncatedChanges.join('\n'),
              fullChanges: JSON.stringify(fullChangesArr),
            });
          }
        }
      }

      // Sort all arrays by key
      added.sort((a, b) => a.key.localeCompare(b.key));
      removed.sort((a, b) => a.key.localeCompare(b.key));
      changed.sort((a, b) => a.key.localeCompare(b.key));

      setDiffResult({ added, removed, changed, oldVersion, newVersion });
    } catch (err) {
      setDiffError(err instanceof Error ? err.message : 'Failed to compare versions');
    } finally {
      setDiffLoading(false);
    }
  }, [selectedForDiff, versions]);

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getEnvironmentBadge = (environment: S3Environment) => {
    const isQa = environment === S3Environment.QA;
    return (
      <Badge className={isQa ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
        {S3EnvironmentLabels[environment]}
      </Badge>
    );
  };

  const renderSkeleton = () => (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Version</TableHead>
            <TableHead>Environment</TableHead>
            <TableHead>Published</TableHead>
            <TableHead>By</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell><Skeleton className="h-4 w-12" /></TableCell>
              <TableCell><Skeleton className="h-6 w-16" /></TableCell>
              <TableCell><Skeleton className="h-4 w-32" /></TableCell>
              <TableCell><Skeleton className="h-4 w-24" /></TableCell>
              <TableCell><Skeleton className="h-4 w-40" /></TableCell>
              <TableCell><Skeleton className="h-8 w-20" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderEmptyState = () => (
    <Card>
      <CardContent className="py-12 text-center">
        <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No versions found</h3>
        <p className="text-muted-foreground mb-4">
          No translations have been published yet for this application.
        </p>
        <Button onClick={() => navigate('/translations/publish')}>
          Publish Translations
        </Button>
      </CardContent>
    </Card>
  );

  const renderVersionsTable = () => (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-[50px]">Compare</TableHead>
            <TableHead className="w-[80px]">Version</TableHead>
            <TableHead className="w-[100px]">Environment</TableHead>
            <TableHead>Published</TableHead>
            <TableHead>By</TableHead>
            <TableHead>Keys</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead className="w-[120px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {versions.map((version) => (
            <TableRow key={version.versionId} className={selectedForDiff.has(version.versionId) ? 'bg-blue-50' : ''}>
              <TableCell>
                <Checkbox
                  checked={selectedForDiff.has(version.versionId)}
                  onCheckedChange={() => handleToggleDiffSelection(version.versionId)}
                  disabled={selectedForDiff.size >= 2 && !selectedForDiff.has(version.versionId)}
                />
              </TableCell>
              <TableCell className="font-mono font-medium">
                v{version.version}
              </TableCell>
              <TableCell>
                {getEnvironmentBadge(version.environment)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  {formatDateTime(version.publishedAt)}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  {version.publishedByName}
                </div>
              </TableCell>
              <TableCell className="text-sm">
                {version.keyCount ?? '—'}
              </TableCell>
              <TableCell className="max-w-[260px] text-sm text-muted-foreground">
                {version.notes ? (
                  <HoverCard openDelay={200}>
                    <HoverCardTrigger asChild>
                      <div className="line-clamp-2 whitespace-pre-wrap break-words cursor-default">
                        {version.notes}
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent align="start" className="w-auto max-w-sm whitespace-pre-wrap break-words text-sm">
                      {version.notes}
                    </HoverCardContent>
                  </HoverCard>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(version.s3Url, '_blank')}
                    title="View JSON"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="container mx-auto py-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Version History</h1>
          <p className="text-muted-foreground">
            View published versions
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/translations/publish')}>
            <Server className="h-4 w-4 mr-2" />
            Publish
          </Button>
          <Button variant="outline" onClick={() => navigate('/translations')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Translations
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <ApplicationTypeSelect
              value={selectedAppType}
              onChange={handleAppTypeChange}
              className="w-[250px]"
              required
            />
            <Select
                value={environmentFilter?.toString() ?? 'all'}
                onValueChange={handleEnvironmentFilterChange}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Environments</SelectItem>
                  <SelectItem value={S3Environment.QA.toString()}>QA Only</SelectItem>
                  <SelectItem value={S3Environment.Production.toString()}>Production Only</SelectItem>
                </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && renderSkeleton()}

      {/* Empty State */}
      {!isLoading && versions.length === 0 && renderEmptyState()}

      {/* Versions Table */}
      {!isLoading && versions.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {versions.length} version{versions.length !== 1 ? 's' : ''}
              {selectedForDiff.size > 0 && (
                <span className="ml-2 text-blue-600">
                  ({selectedForDiff.size} selected for comparison)
                </span>
              )}
            </p>
            <div className="flex gap-2">
              {selectedForDiff.size > 0 && (
                <Button variant="outline" size="sm" onClick={handleClearDiffSelection}>
                  Clear Selection
                </Button>
              )}
              {selectedForDiff.size === 2 && (
                <Button size="sm" onClick={handleCompareVersions}>
                  <GitCompare className="h-4 w-4 mr-2" />
                  Compare Versions
                </Button>
              )}
            </div>
          </div>
          {renderVersionsTable()}
        </div>
      )}

      {/* Version Diff Dialog */}
      <Dialog open={diffDialogOpen} onOpenChange={setDiffDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              Compare Versions
            </DialogTitle>
            {diffResult && (
              <DialogDescription>
                Comparing v{diffResult.oldVersion.version} → v{diffResult.newVersion.version}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            {diffLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="ml-3 text-muted-foreground">Loading version data...</span>
              </div>
            )}

            {diffError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{diffError}</AlertDescription>
              </Alert>
            )}

            {diffResult && !diffLoading && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="flex gap-4 p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <Plus className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-700">{diffResult.added.length} added</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Minus className="h-4 w-4 text-red-600" />
                    <span className="font-medium text-red-700">{diffResult.removed.length} removed</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <RefreshCw className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-blue-700">{diffResult.changed.length} changed</span>
                  </div>
                </div>

                {/* No changes */}
                {diffResult.added.length === 0 && diffResult.removed.length === 0 && diffResult.changed.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No differences found between these versions.
                  </div>
                )}

                {/* Diff content */}
                <ScrollArea className="h-[400px] border rounded-lg">
                  <div className="p-4 space-y-6">
                    {/* Added keys */}
                    {diffResult.added.length > 0 && (
                      <div>
                        <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          Added Keys ({diffResult.added.length})
                        </h4>
                        <div className="space-y-2">
                          {diffResult.added.map((item) => (
                            <div key={item.key} className="p-2 bg-green-50 border border-green-200 rounded text-sm">
                              <div className="font-mono font-medium text-green-800">{item.key}</div>
                              <div className="text-green-700 mt-1 break-words">{item.newValue}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Removed keys */}
                    {diffResult.removed.length > 0 && (
                      <div>
                        <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                          <Minus className="h-4 w-4" />
                          Removed Keys ({diffResult.removed.length})
                        </h4>
                        <div className="space-y-2">
                          {diffResult.removed.map((item) => (
                            <div key={item.key} className="p-2 bg-red-50 border border-red-200 rounded text-sm">
                              <div className="font-mono font-medium text-red-800">{item.key}</div>
                              <div className="text-red-700 mt-1 break-words">{item.oldValue}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Changed keys */}
                    {diffResult.changed.length > 0 && (
                      <div>
                        <h4 className="font-medium text-blue-700 mb-2 flex items-center gap-2">
                          <RefreshCw className="h-4 w-4" />
                          Changed Keys ({diffResult.changed.length})
                        </h4>
                        <div className="space-y-2">
                          {diffResult.changed.map((item) => {
                            const isExpanded = expandedChanges.has(item.key);
                            return (
                              <div key={item.key} className="p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                                <button
                                  className="w-full flex items-center gap-2 text-left"
                                  onClick={() => {
                                    setExpandedChanges((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(item.key)) {
                                        next.delete(item.key);
                                      } else {
                                        next.add(item.key);
                                      }
                                      return next;
                                    });
                                  }}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                  )}
                                  <span className="font-mono font-medium text-blue-800">{item.key}</span>
                                </button>
                                <div className="mt-2 ml-6 space-y-1">
                                  {isExpanded ? (
                                    // Expanded: show full changes with better formatting
                                    (() => {
                                      try {
                                        const fullChangesData = JSON.parse(item.fullChanges) as { langCode: string; changes: { field: string; oldText: string; newText: string }[] }[];
                                        return fullChangesData.map((langData, idx) => (
                                          <div key={idx} className="bg-white/70 p-2 rounded border border-blue-100">
                                            <div className="font-medium text-blue-700 mb-2">[{langData.langCode}]</div>
                                            <div className="space-y-2">
                                              {langData.changes.map((change, changeIdx) => (
                                                <div key={changeIdx} className="text-xs">
                                                  {change.field && (
                                                    <div className="font-medium text-gray-600 mb-1">{change.field}:</div>
                                                  )}
                                                  <div className="bg-red-50 p-1.5 rounded text-red-700 break-all whitespace-pre-wrap mb-1">
                                                    <span className="text-red-500 font-medium">- </span>{change.oldText}
                                                  </div>
                                                  <div className="bg-green-50 p-1.5 rounded text-green-700 break-all whitespace-pre-wrap">
                                                    <span className="text-green-500 font-medium">+ </span>{change.newText}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ));
                                      } catch {
                                        // Fallback for non-JSON format
                                        return <div className="text-xs text-gray-500">Unable to parse changes</div>;
                                      }
                                    })()
                                  ) : (
                                    // Collapsed: show truncated summary
                                    item.newValue.split('\n').map((change, idx) => (
                                      <div key={idx} className="text-xs bg-white/50 p-1.5 rounded border border-blue-100 truncate">
                                        {change}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDiffDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
